import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionItemTemplateActions from "../../../actions/trialInstitutionItemTemplate";
import { NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import { CheckboxFormInput, NumberFormInput, SelectFormInput, TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { CollectionPage } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionItemTemplateBreadcrumbs, TrialInstitutionItemTemplateCreateBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { SettingsTrialSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialInstitutionItemTemplatePage.scss";

interface TrialInstitutionItemTemplatePageProps {
    form: Dtos.TrialInstitutionItemTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialInstitutionItemTemplate: Dtos.TrialInstitutionItemTemplate;
    collection: Dtos.Collection;
    lookups: Dtos.Lookup[];
    loadingTrialInstitutionItemTemplate: boolean;
    loadTrialInstitutionItemTemplateFailure: boolean;
    loadTrialInstitutionItemTemplateErrorCode: string;
    savingTrialInstitutionItemTemplate: boolean;
    saveTrialInstitutionItemTemplateSuccess: boolean;
    saveTrialInstitutionItemTemplateFailure: boolean;
    creatingTrialInstitutionItemTemplate: boolean;
    createTrialInstitutionItemTemplateSuccess: boolean;
    validatingTrialInstitutionItemTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    itemComponentTypes: Dtos.ItemComponentType[];
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    clearTrialInstitutionItemTemplate: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateClearActionCreator;
    loadTrialInstitutionItemTemplate: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateLoadByIdActionCreator;
    saveTrialInstitutionItemTemplate: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateSaveActionCreator;
    createTrialInstitutionItemTemplate: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateCreateActionCreator;
    validateTrialInstitutionItemTemplate: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
}

const reduxFormName: string = "reduxForms.trialInstitutionItemTemplate";

const TrialInstitutionItemTemplatePage: FC<TrialInstitutionItemTemplatePageProps> = ({
    form,
    formState,
    formProperties,
    trialInstitutionItemTemplate,
    collection,
    lookups,
    loadingTrialInstitutionItemTemplate,
    loadTrialInstitutionItemTemplateFailure,
    loadTrialInstitutionItemTemplateErrorCode,
    savingTrialInstitutionItemTemplate,
    saveTrialInstitutionItemTemplateSuccess,
    saveTrialInstitutionItemTemplateFailure,
    creatingTrialInstitutionItemTemplate,
    createTrialInstitutionItemTemplateSuccess,
    validatingTrialInstitutionItemTemplate,
    validationFailures,
    permissions,
    itemComponentTypes,
    loadingItemComponentTypes,
    // loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    clearTrialInstitutionItemTemplate,
    loadTrialInstitutionItemTemplate,
    saveTrialInstitutionItemTemplate,
    createTrialInstitutionItemTemplate,
    validateTrialInstitutionItemTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearItemComponentTypes,
    loadItemComponentTypes
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{trialId: string;}>();
    const trialIdNumber = parseInt(trialId, 10);
    const { trialInstitutionItemTemplateId } = useParams<{trialInstitutionItemTemplateId: string;}>();
    const trialInstitutionItemTemplateIdNumber = parseInt(trialInstitutionItemTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialInstitutionItemTemplate = {} as Dtos.TrialInstitutionItemTemplate;

        if (!isNaN(trialIdNumber)) {
            extraData.trialId = trialIdNumber;
        }

        loadForm(reduxFormName, Object.assign({ ...trialInstitutionItemTemplate }, extraData));
    };


    // Load trial institution item template
    useEffect(() => {
        if (
            trialInstitutionItemTemplateIdNumber &&
            !trialInstitutionItemTemplate &&
            !loadingTrialInstitutionItemTemplate
        ) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(
                TrialInstitutionItemTemplateBreadcrumbs(
                    trialInstitutionItemTemplateIdNumber,
                    isNaN(trialIdNumber) ? undefined : trialIdNumber
                )
            );

            loadTrialInstitutionItemTemplate(trialInstitutionItemTemplateIdNumber);
        } else if (
            !trialInstitutionItemTemplateIdNumber &&
            !creatingTrialInstitutionItemTemplate &&
            !createTrialInstitutionItemTemplateSuccess
        ) {
            createTrialInstitutionItemTemplate(isNaN(trialIdNumber) ? undefined : trialIdNumber);
            setBreadcrumbs(
                TrialInstitutionItemTemplateCreateBreadcrumbs(isNaN(trialIdNumber) ? undefined : trialIdNumber)
            );
            setDrawerItems([]);
        } else if (trialInstitutionItemTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }

        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }
    }, [
        trialInstitutionItemTemplateIdNumber,
        trialInstitutionItemTemplate,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialInstitutionItemTemplateBreadcrumbs,
        loadTrialInstitutionItemTemplate,
        createTrialInstitutionItemTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialInstitutionItemTemplate,
        creatingTrialInstitutionItemTemplate,
        createTrialInstitutionItemTemplateSuccess,
        TrialInstitutionItemTemplateCreateBreadcrumbs,
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionItemTemplateFailure) {
            navigate(`/error/${loadTrialInstitutionItemTemplateErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadTrialInstitutionItemTemplateFailure,
        loadTrialInstitutionItemTemplateErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialInstitutionItemTemplateIdNumber && trialInstitutionItemTemplate?.name) {
            setBreadcrumbs(
                TrialInstitutionItemTemplateBreadcrumbs(
                    trialInstitutionItemTemplateIdNumber,
                    isNaN(trialIdNumber) ? undefined : trialIdNumber,
                    trialInstitutionItemTemplate?.name
                )
            );
        }
    }, [
        trialInstitutionItemTemplateIdNumber,
        trialInstitutionItemTemplate,
        setBreadcrumbs,
        TrialInstitutionItemTemplateBreadcrumbs,
    ]);

    // Reload trial item template after saving
    useEffect(() => {
        if (!savingTrialInstitutionItemTemplate && saveTrialInstitutionItemTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial item template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = trialIdNumber
                    ? `/settings/trialinstitution/itemtemplates/${trialIdNumber}`
                    : "/settings/trialinstitution/itemtemplates";
                navigate(url);
            } else if (!trialInstitutionItemTemplateIdNumber) {
                navigate(
                    `/settings/trialinstitution/itemtemplates/${trialInstitutionItemTemplate?.trialId}/template/${trialInstitutionItemTemplate?.id}`
                );
                setInitalised(false);
                clearTrialInstitutionItemTemplate();
            }
        }
    }, [
        savingTrialInstitutionItemTemplate,
        saveTrialInstitutionItemTemplateSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialInstitutionItemTemplate && saveTrialInstitutionItemTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialInstitutionItemTemplate,
        saveTrialInstitutionItemTemplateFailure,
    ]);

    // Cleanup effect to clear the trial institution item template on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitutionItemTemplate();
            clearItemComponentTypes();
            resetForm(reduxFormName);
        };
    }, [
        clearTrialInstitutionItemTemplate,
        clearItemComponentTypes,
        resetForm,
        reduxFormName,
    ]);

    const onCancel = () => {
        let url: string = trialIdNumber
            ? `/settings/trialinstitution/itemtemplates/${trialIdNumber}`
            : "/settings/trialinstitution/itemtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialInstitutionItemTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialInstitutionItemTemplate = {} as Dtos.TrialInstitutionItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!data?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            await saveTrialInstitutionItemTemplate(
                Object.assign({ ...data }, changes),
                Object.assign({ ...collection }, collectionChanges)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialInstitutionItemTemplate = {} as Dtos.TrialInstitutionItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!form?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialInstitutionItemTemplate
            ) {
                await validateTrialInstitutionItemTemplate(
                    Object.assign({ ...form }, changes),
                    Object.assign({ ...collection }, collectionChanges)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderCollection = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialInstitutionItemTemplatesUpdate,
                Dtos.Permission.TrialInstitutionItemTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialInstitutionItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        if (
            form &&
            !loadingTrialInstitutionItemTemplate &&
            itemComponentTypes
        ) {
            return (
                <CollectionPage
                    itemComponentTypeId={
                        itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id
                    }
                    collectionId={form.collectionId || 0}
                    readonly={formDisabled}
                />
            );
        }
    }, [
        form,
        permissions,
        savingTrialInstitutionItemTemplate,
        formState,
        loadingTrialInstitutionItemTemplate,
        itemComponentTypes,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialInstitutionItemTemplatesUpdate,
                Dtos.Permission.TrialInstitutionItemTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialInstitutionItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow">
                        <div className="form-row pt-4 pb-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        name: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        description: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextAreaFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                rows: 3,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        trialInstitutionItemTemplateCategoryId:
                                            {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "TrialInstitutionItemTemplate",
                                                                "TrialInstitutionItemTemplateCategoryId",
                                                                lookups
                                                            ),
                                                            form.trialInstitutionItemTemplateCategoryId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2",
                                                fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field mr-0 pr-0",
                                            },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        rank: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: NumberFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pt-sm-0 pt-md-2 pt-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        trialId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled || !isNaN(trialIdNumber),
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "TrialInstitutionItemTemplate",
                                                            "TrialId",
                                                            lookups
                                                        ),
                                                        form.trialId
                                                    ),
                                                clearable: true,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        allowIntraVersions: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow intra versions",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                            changeAction: (_model, value, _form,  _parentModel) => {
                                                changeForm(`${reduxFormName}.allowIntraVersions`, value);
                                                if (!value) {
                                                    changeForm(`${reduxFormName}.initialVersion`, null);
                                                }
                                            },
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        initialVersion: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled || !form.allowIntraVersions,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row pt-2 pb-4 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        allowVersionChanges: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow version label changes",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName:  "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialInstitutionItemTemplate",
                                    {
                                        active: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Active",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pt-sm-0 pt-md-2 pt-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            components: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Components</h1>
                        {renderCollection}
                    </>
                ),
            },
        };

        return (
            <div>
                <h1>Trial Institution Item Template</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialInstitutionItemTemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialInstitutionItemTemplate,
        formState,
        lookups,
        generateOptionsFromLookup,
        findLookup,
        renderCollection,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        trialIdNumber,
    ]);

    return (
        <TrialLookupsLayout
            loading={
                loadingTrialInstitutionItemTemplate ||
                creatingTrialInstitutionItemTemplate
            }
            permissions={permissions}
            permission={Dtos.Permission.TrialInstitutionItemTemplatesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialInstitutionItemTemplatePageProps> => {
    let trialInstitutionItemTemplate: Dtos.TrialInstitutionItemTemplate | undefined = 
        !(state.trialInstitutionItemTemplate.formData instanceof Array)
            ? state.trialInstitutionItemTemplate.formData
            : undefined;
    let collection: Dtos.Collection | undefined = !(state.reduxForms.collection instanceof Array)
        ? state.reduxForms.collection
        : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];
    let itemComponentTypes = state.itemComponentTypes.formData instanceof Array
        ? state.itemComponentTypes.formData
        : undefined;

    return {
        trialInstitutionItemTemplate: trialInstitutionItemTemplate!,
        collection: collection!,
        loadingTrialInstitutionItemTemplate: 
            state.trialInstitutionItemTemplate.loadState && 
            state.trialInstitutionItemTemplate.loadState.status == RequestState.Pending,
        loadTrialInstitutionItemTemplateFailure: 
            state.trialInstitutionItemTemplate.loadState &&
            state.trialInstitutionItemTemplate.loadState.status == RequestState.Failure,
        loadTrialInstitutionItemTemplateErrorCode: state.trialInstitutionItemTemplate.loadState.errorCode,
        savingTrialInstitutionItemTemplate:
            state.trialInstitutionItemTemplate.saveState &&
            state.trialInstitutionItemTemplate.saveState.status == RequestState.Pending,
        saveTrialInstitutionItemTemplateSuccess:
            state.trialInstitutionItemTemplate.saveState &&
            state.trialInstitutionItemTemplate.saveState.status == RequestState.Success,
        saveTrialInstitutionItemTemplateFailure:
            state.trialInstitutionItemTemplate.saveState &&
            state.trialInstitutionItemTemplate.saveState.status == RequestState.Failure,
        creatingTrialInstitutionItemTemplate:
            state.trialInstitutionItemTemplate.createState &&
            state.trialInstitutionItemTemplate.createState.status == RequestState.Pending,
        createTrialInstitutionItemTemplateSuccess:
            state.trialInstitutionItemTemplate.createState &&
            state.trialInstitutionItemTemplate.createState.status == RequestState.Success,
        validatingTrialInstitutionItemTemplate:
            state.trialInstitutionItemTemplate.validateState &&
            state.trialInstitutionItemTemplate.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.trialInstitutionItemTemplate.lookups,
        form: state.reduxForms.trialInstitutionItemTemplate,
        formState: state.trialInstitutionItemTemplate.formState,
        formProperties: state.trialInstitutionItemTemplate.formProperties,
        validationFailures: trialInstitutionItemTemplate
            ? state.trialInstitutionItemTemplate.validationFailures
            : undefined,
        itemComponentTypes: itemComponentTypes,
        loadingItemComponentTypes:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialInstitutionItemTemplatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialInstitutionItemTemplate: bindActionCreators(
            TrialInstitutionItemTemplateActions.LoadTrialInstitutionItemTemplateById,
            dispatch
        ),
        clearTrialInstitutionItemTemplate: bindActionCreators(
            TrialInstitutionItemTemplateActions.Clear,
            dispatch
        ),
        saveTrialInstitutionItemTemplate: bindActionCreators(
            TrialInstitutionItemTemplateActions.SaveTrialInstitutionItemTemplate,
            dispatch
        ),
        createTrialInstitutionItemTemplate: bindActionCreators(
            TrialInstitutionItemTemplateActions.CreateTrialInstitutionItemTemplate,
            dispatch
        ),
        validateTrialInstitutionItemTemplate: bindActionCreators(
            TrialInstitutionItemTemplateActions.ValidateTrialInstitutionItemTemplate,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialInstitutionItemTemplatePage);
